// Room data schema — first-class, validated, authored data. NO room-specific
// engine code; everything the engine needs about a room lives here.
// See research/DECISIONS.md, Decision 6.
import { z } from "zod";

const Vec2 = z.object({ x: z.number(), y: z.number() });
const Poly = z.array(Vec2).min(3);

export const TransitionType = z.enum(["fade", "edge-pan", "cut"]);

// Context-sensitive interaction verbs. Drive the prompt glyph + (later) the script.
export const Verb = z.enum(["examine", "use", "take", "talk", "kindle"]);

// ---- Room state (puzzles) ----
// A state value is a flag/number/label. Rooms carry a small named state object.
export const StateVal = z.union([z.boolean(), z.number(), z.string()]);

// A condition over state. `scope` picks the store: "room" (resets on entry, default) or
// "global" (persists across rooms — saga/quest flags). Holds when store[key] === eq, or
// (eq omitted) when store[key] is truthy. `minFlame` additionally requires the carried
// flame to be at least that bright (the carried flame is the global numeric `flame`). A
// condition may be key-based, flame-based, or both (AND).
export const CondSchema = z
  .object({
    key: z.string().optional(),
    eq: StateVal.optional(),
    scope: z.enum(["room", "global"]).default("room"),
    minFlame: z.number().optional(),
  })
  .refine((c) => c.key !== undefined || c.minFlame !== undefined, {
    message: "condition needs a key or a minFlame",
  });

// A declarative effect applied when an interactable fires (the ONLY way to mutate state).
// set/toggle target ROOM state; setGlobal/toggleGlobal target persistent GLOBAL state;
// `flame` adds to the carried flame (kindling a hearth grows it). Declarative + validatable.
export const EffectSchema = z.object({
  set: z.record(StateVal).optional(),
  toggle: z.array(z.string()).optional(),
  setGlobal: z.record(StateVal).optional(),
  toggleGlobal: z.array(z.string()).optional(),
  flame: z.number().optional(),
});

// A conditionally-walkable area folded into the navmesh ONLY when its condition holds
// (a drawbridge lowered, a sluice drained, a gate opened).
export const GatedAreaSchema = z.object({
  id: z.string(),
  polygon: Poly,
  gatedBy: CondSchema,
});

export const HotspotSchema = z.object({
  id: z.string(),
  polygon: Poly,
  label: z.string().optional(),
  /** PROSE shown when you examine/interact (text-first; elevated register). 1-3 sentences. */
  line: z.string().optional(),
  cursor: z.string().optional(),
  priority: z.number().default(0),
  /** what interacting does, in the wordless vocabulary; default "examine" */
  verb: Verb.default("examine"),
  /** explicit walkable stand-point to interact from; defaults to clamped centroid */
  interactAt: Vec2.optional(),
  /** only active (hoverable/armable/interactable) when this condition holds */
  gatedBy: CondSchema.optional(),
  /** state mutation applied when interacted with */
  effect: EffectSchema.optional(),
});

// Placeholder occluder: a colored rect with a feet-baseline for y-sort.
// (Real rooms will swap the rect for a sprite; the baseline stays.)
export const OccluderSchema = z.object({
  id: z.string(),
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  baselineY: z.number(),
  color: z.number().default(0x4a4a63),
});

// ---- Decor / set-dressing shape layer (PURELY VISUAL — never affects nav or determinism) ----
// Arbitrary labeled silhouettes that compose the backdrop & dressing so the greybox reads as a
// real (MI1-style) room AND can drive an image generator: each decor element carries a SEMANTIC
// `kind` (what it depicts) plus a shape that is NOT limited to rectangles. This is the answer to
// "we need shapes other than rectangles for the art reference." Tall props that the hero should
// walk behind still use `occluders` (depth-sorted); decor draws as composed backdrop.
const Pt = z.tuple([z.number(), z.number()]);
export const ShapeSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("poly"), points: z.array(Pt).min(3) }),
  z.object({ type: z.literal("rect"), x: z.number(), y: z.number(), w: z.number(), h: z.number() }),
  z.object({ type: z.literal("ellipse"), cx: z.number(), cy: z.number(), rx: z.number(), ry: z.number() }),
  // arc/wedge: an elliptical sweep from angle a0→a1 (radians), closed to centre (for bowls/arches)
  z.object({ type: z.literal("arc"), cx: z.number(), cy: z.number(), rx: z.number(), ry: z.number(), a0: z.number(), a1: z.number() }),
]);
export const DecorSchema = z.object({
  id: z.string(),
  kind: z.string(), // free-form semantic label: fountain | arch | banner | hearth | pillar | tree | way_stone | wayshrine | window | beam | wall | horizon | sky | ...
  shape: ShapeSchema,
  color: z.number().default(0x4a4a63),
  alpha: z.number().min(0).max(1).default(1),
  /** draw order WITHIN the decor layer (higher = in front); the whole layer sits behind actors */
  z: z.number().default(0),
  /** optional: a feet-line, recorded for the future art-swap/depth pass (unused by greybox render) */
  baselineY: z.number().optional(),
  /** RICH, SPECIFIC visual description of what this silhouette depicts — a regional prompt for the
   * image generator (material, form, light, wear, colour). The greybox ignores it; the art pass uses it. */
  desc: z.string().optional(),
});

// ---- Living-scene effects layer (PURELY VISUAL — never affects nav or determinism) ----
// Declarative ambient animation placed in room-space: flickering flames, rising embers, a warm
// glow, water shimmer, floating dust-motes, drifting clouds, flying birds. Drawn PROCEDURALLY and
// time-driven (deterministic per position via a hash, NOT Math.random), so they cost no assets,
// stay pixel-crisp, and never touch the simulation/probe state. `layer` puts an effect behind
// ("back") or in front of ("front") the actors. A region is (x,y) top-left + (w,h) extent;
// point/área effects (glow) use the region centre + size.
export const FxKind = z.enum(["flames", "embers", "glow", "shimmer", "motes", "clouds", "birds"]);
export const FxSchema = z.object({
  id: z.string(),
  kind: FxKind,
  x: z.number().default(0),
  y: z.number().default(0),
  w: z.number().default(40),
  h: z.number().default(40),
  color: z.number().optional(), // base tint (each kind has a sensible default)
  count: z.number().default(14), // particle/element count where relevant
  intensity: z.number().default(1), // size/speed/brightness multiplier
  layer: z.enum(["back", "front"]).default("back"),
  seed: z.number().default(1), // phase offset so identical emitters animate out of step
});

// ---- Wordless dialogue ----
// A fixed pictographic vocabulary (drawn procedurally — no fonts, no external atlas).
// Dialogue is sequences of these glyphs, never words.
export const Glyph = z.enum([
  "greet", "bye", "question", "yes", "no", "help", "thanks",
  "light", "dark", "key", "door", "path", "up", "down",
  "home", "far", "heart", "danger", "water", "gift", "wait", "follow", "star",
]);
// Mood tints the bubble (warmth/wariness) — part of the wordless read.
export const Mood = z.enum(["neutral", "warm", "wary", "sad", "glad"]);

// Dialogue is TEXT-FIRST: each line is spoken prose (`text`) + a mood. `glyphs` are now optional
// decoration only (legacy pictographs). A line needs at least one of text/glyphs.
export const DialogLineSchema = z
  .object({
    text: z.string().optional(),
    glyphs: z.array(Glyph).optional(),
    mood: Mood.default("neutral"),
  })
  .refine((d) => d.text !== undefined || (d.glyphs !== undefined && d.glyphs.length > 0), {
    message: "a dialog line needs text or glyphs",
  });

// Non-player characters: idle obstacles (depth-sorted, separated), optionally talkable.
export const NpcSchema = z.object({
  id: z.string(),
  x: z.number(),
  y: z.number(),
  radius: z.number().default(8),
  color: z.number().default(0x7a5c5c),
  label: z.string().optional(),
  /** patrol waypoints (looped). Present => the NPC wanders (movable, separated). */
  patrol: z.array(Vec2).optional(),
  speed: z.number().default(50),
  /** wordless dialogue shown as a speech bubble on `talk`; advanced by interact/click */
  dialog: z.array(DialogLineSchema).optional(),
});

export const ExitSchema = z.object({
  id: z.string(),
  polygon: Poly,
  target: z.string(),
  spawn: Vec2,
  transition: TransitionType.default("fade"),
  /** for edge-pan: where the next room sits relative to this one (world offset) */
  pan: Vec2.optional(),
  /** locked until this condition holds — clicking gives feedback but won't transition */
  gatedBy: CondSchema.optional(),
});

export const RoomSchema = z.object({
  id: z.string(),
  /** the room's title, shown as an entry card (text-first). */
  name: z.string().optional(),
  size: z.object({ w: z.number(), h: z.number() }),
  background: z.object({ color: z.number() }),
  /** painted backdrop image (the art-swap layer): a URL/path filling the room w×h, drawn behind
   * the floor + actors. When present the greybox `decor` is hidden (the painting replaces it). */
  bgImage: z.string().optional(),
  /** RICH, SPECIFIC prose art-direction for the WHOLE painted room — the master image-generator
   * prompt (composition, palette, light, materials, weather, mood; MI1 hand-painted VGA register).
   * Greybox ignores it; it travels with the room data so the art pass can paint to it. */
  art: z.string().optional(),
  spawn: Vec2,
  actorRadius: z.number().default(8),
  /** how close the hero's feet must be to an interactable's stand-point to "arm" it */
  interactRadius: z.number().default(40),
  walkmesh: z.array(Poly).min(1),
  /** initial room-local puzzle state (flags/phases). Mutated only via interaction effects. */
  state: z.record(StateVal).default({}),
  /** extra walkable areas folded into the navmesh when their condition holds */
  gatedAreas: z.array(GatedAreaSchema).default([]),
  hotspots: z.array(HotspotSchema).default([]),
  /** purely-visual backdrop/set-dressing silhouettes (labeled, any shape) — see DecorSchema */
  decor: z.array(DecorSchema).default([]),
  /** living-scene ambient effects (flames/embers/glow/water/motes/clouds/birds) — see FxSchema */
  fx: z.array(FxSchema).default([]),
  occluders: z.array(OccluderSchema).default([]),
  npcs: z.array(NpcSchema).default([]),
  exits: z.array(ExitSchema).default([]),
});

export type Room = z.infer<typeof RoomSchema>;
export type Verb = z.infer<typeof Verb>;
export type Glyph = z.infer<typeof Glyph>;
export type Mood = z.infer<typeof Mood>;
export type DialogLine = z.infer<typeof DialogLineSchema>;
export type StateVal = z.infer<typeof StateVal>;
export type Cond = z.infer<typeof CondSchema>;
export type Effect = z.infer<typeof EffectSchema>;
export type GatedArea = z.infer<typeof GatedAreaSchema>;
export type Hotspot = z.infer<typeof HotspotSchema>;
export type Shape = z.infer<typeof ShapeSchema>;
export type Decor = z.infer<typeof DecorSchema>;
export type Fx = z.infer<typeof FxSchema>;
export type Occluder = z.infer<typeof OccluderSchema>;
export type Npc = z.infer<typeof NpcSchema>;
export type Exit = z.infer<typeof ExitSchema>;

export function loadRoom(data: unknown): Room {
  return RoomSchema.parse(data);
}
